import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PublicAccountSummary from "./PublicAccountSummary";
import { PublicSessionOtpForm } from "./ParagraphPracticePage";
import {
  PUBLIC_SESSION_CONFIG,
  getPublicSession,
  getPublicSessionPayload,
  isPublicSessionError,
  logoutPublicSession,
} from "./publicAccountSession";
import "./StudyBuddyPage.css";
import { studyBuddyPracticeUrl } from "./studyBuddyPracticeSelection";
import PublishedStudyPackLibrary from "./PublishedStudyPackLibrary";
import { PackResults, StudyBuddyCreditGuide, StudyBuddyPracticeScope } from "./StudyBuddyPackComponents";
import StudyBuddyPackEditor from "./StudyBuddyPackEditor";

export const STUDY_BUDDY_POLL_MS = 3000;
export const STUDY_BUDDY_COST = 20;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_POLL_FAILURES = 3;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;
const ACCEPTED_TYPES = new Set(["application/pdf", "image/png", "image/jpeg", "image/webp"]);

export function hasEnoughStudyBuddyCredits(balances) {
  if (!balances) return false;
  const safeAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  };
  const subscription = safeAmount(balances.subscription_remaining);
  const promotional = safeAmount(balances.promotional_credit_balance);
  const paid = safeAmount(balances.paid_credit_balance);
  return subscription >= STUDY_BUDDY_COST || promotional + paid >= STUDY_BUDDY_COST;
}

function payload(response) {
  const value = response?.data || response || {};
  return value?.data && typeof value.data === "object" ? { ...value, ...value.data } : value;
}

function isPdf(file) {
  return file?.type === "application/pdf" || file?.name?.toLowerCase().endsWith(".pdf");
}

function isImage(file) {
  return ["image/png", "image/jpeg", "image/webp"].includes(file?.type);
}

export function validateStudyBuddyFiles(files) {
  if (!files.length) return "Please select a PDF or note images.";
  if (files.length > MAX_FILES) return "Select no more than 10 note images.";
  if (files.some((file) => !ACCEPTED_TYPES.has(file.type))) return "Use a PDF, PNG, JPEG or WEBP file.";
  if (files.some((file) => file.size === 0)) return "One or more selected files are empty or unreadable.";
  if (files.some((file) => file.size > MAX_FILE_BYTES)) return "Each file must be 20 MB or smaller.";
  if (files.reduce((total, file) => total + file.size, 0) > MAX_FILE_BYTES) return "The total upload must be 20 MB or smaller.";
  const pdfs = files.filter(isPdf);
  if (pdfs.length && (pdfs.length > 1 || files.length > 1)) return "Select one PDF or a set of images, not both.";
  if (!pdfs.length && files.some((file) => !isImage(file))) return "Use a PDF, PNG, JPEG or WEBP file.";
  return "";
}

function safeError(error, fallback) {
  const data = payload(error?.response);
  const code = data.error_code || data.error || data.code || "";
  const messages = {
    no_file_selected: "Please select a PDF or note images.",
    unsupported_file_type: "Use a PDF, PNG, JPEG or WEBP file.",
    mixed_file_types: "Select one PDF or a set of images, not both.",
    too_many_files: "Select no more than 10 note images.",
    too_many_pages: "Select a PDF with no more than 10 pages or no more than 10 note images.",
    file_too_large: "Each file must be 20 MB or smaller.",
    upload_too_large: "The total upload must be 20 MB or smaller.",
    unreadable_file: "One or more files could not be read. Check the files and try again.",
    pack_not_found: "This study pack could not be found.",
  };
  return messages[code] || fallback;
}

function countLabel(value, singular, plural) {
  const count = Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : 0;
  return `${count} ${count === 1 ? singular : plural}`;
}

function createdDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-SG", { day: "numeric", month: "long", year: "numeric" }).format(date);
}

export function StudyBuddyWorkspace({ embed = false, onPracticeNavigate = () => {} }) {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [balances, setBalances] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [packId, setPackId] = useState("");
  const [status, setStatus] = useState("");
  const [pack, setPack] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [pollPaused, setPollPaused] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const [savedPacks, setSavedPacks] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoaded, setListLoaded] = useState(false);
  const [listError, setListError] = useState("");
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [selectedMode, setSelectedMode] = useState("");
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState("");
  const [practiceSelection, setPracticeSelection] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [notice, setNotice] = useState("");
  const pollTimer = useRef(null);
  const pollRunning = useRef(false);
  const pollFailures = useRef(0);
  const pollStartedAt = useRef(0);
  const mounted = useRef(true);
  const balanceRefreshPack = useRef(null);
  const listTimer = useRef(null);
  const listRequestRunning = useRef(false);
  const deleteButtonRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  }, []);

  const clearListPoll = useCallback(() => {
    if (listTimer.current) clearTimeout(listTimer.current);
    listTimer.current = null;
  }, []);

  const expire = useCallback(() => {
    clearPoll();
    clearListPoll();
    balanceRefreshPack.current = null;
    setSavedPacks([]); setListLoaded(false); setListLoading(false); setListError("");
    setSelectedPack(null); setSelectedPackId(""); setSelectedMode(""); setSelectedError(""); setPracticeSelection(null); setDeleteTarget(null); setDeleteError("");
    setUser(null); setBalances(null); setUploading(false); setPackId(""); setStatus(""); setPack(null); setExpanded(new Set()); setPollPaused(false);
    setError("Your session has expired. Please verify your email again.");
  }, [clearListPoll, clearPoll]);

  const refreshBalance = useCallback(async () => {
    try {
      const session = getPublicSessionPayload(await getPublicSession());
      if (!mounted.current) return;
      if (session.authenticated) setBalances(session.balances);
      else expire();
    } catch (_) {
      // A temporary balance refresh failure must not replace pack state or errors.
    }
  }, [expire]);

  const loadPackList = useCallback(async ({ background = false } = {}) => {
    if (listRequestRunning.current) return;
    listRequestRunning.current = true;
    if (!background) { setListLoading(true); setListError(""); }
    try {
      const response = await axios.get("/api/study-buddy/packs", PUBLIC_SESSION_CONFIG);
      if (!mounted.current) return;
      const data = payload(response);
      setSavedPacks(Array.isArray(data.packs) ? data.packs.filter((item) => item && typeof item === "object") : []);
      setListLoaded(true); setListError("");
    } catch (requestError) {
      if (!mounted.current) return;
      if (isPublicSessionError(requestError)) expire();
      else if (!background || !listLoaded) setListError("We could not load your study packs. Please try again.");
    } finally {
      listRequestRunning.current = false;
      if (mounted.current) setListLoading(false);
    }
  }, [expire, listLoaded]);

  useEffect(() => {
    mounted.current = true;
    getPublicSession().then((response) => {
      if (!mounted.current) return;
      const session = getPublicSessionPayload(response);
      if (session.authenticated && session.user) { setUser(session.user); setBalances(session.balances); }
    }).catch(() => { if (mounted.current) setError("We could not check your account. Please try again."); })
      .finally(() => { if (mounted.current) setChecking(false); });
    return () => { mounted.current = false; clearPoll(); clearListPoll(); };
  }, [clearListPoll, clearPoll]);

  useEffect(() => {
    if (user && !listLoaded && !listRequestRunning.current) loadPackList();
  }, [listLoaded, loadPackList, user]);

  useEffect(() => {
    clearListPoll();
    if (!user || !savedPacks.some((item) => String(item.status).toLowerCase() === "processing")) return undefined;
    listTimer.current = setTimeout(async () => {
      await loadPackList({ background: true });
    }, STUDY_BUDDY_POLL_MS);
    return clearListPoll;
  }, [clearListPoll, loadPackList, savedPacks, user]);

  useEffect(() => {
    if (!user) return undefined;
    const refresh = () => { refreshBalance(); loadPackList({ background: true }); };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [loadPackList, refreshBalance, user]);

  useEffect(() => {
    if (deleteTarget) deleteButtonRef.current?.focus();
  }, [deleteTarget]);

  const retrievePack = useCallback(async (id) => {
    try {
      const response = await axios.get(`/api/study-buddy/packs/${encodeURIComponent(id)}`, PUBLIC_SESSION_CONFIG);
      if (mounted.current) { setPack(payload(response)); setError(""); }
    } catch (requestError) {
      if (!mounted.current) return;
      if (isPublicSessionError(requestError)) expire();
      else setError(safeError(requestError, "Your study pack is ready, but could not be loaded. Please try again."));
    }
  }, [expire]);

  const checkStatus = useCallback(async (id, manual = false) => {
    clearPoll();
    if (!id || pollRunning.current) return;
    if (manual) { pollFailures.current = 0; setPollPaused(false); setError(""); }
    pollRunning.current = true;
    try {
      const response = await axios.get(`/api/study-buddy/packs/${encodeURIComponent(id)}/status`, PUBLIC_SESSION_CONFIG);
      if (!mounted.current) return;
      pollFailures.current = 0;
      const statusData = payload(response);
      const next = String(statusData.status || "processing").toLowerCase();
      setStatus(next);
      if (!balanceRefreshPack.current || balanceRefreshPack.current.id !== id) {
        balanceRefreshPack.current = { id, initial: false, terminal: false };
      }
      if (!balanceRefreshPack.current.initial) {
        balanceRefreshPack.current.initial = true;
        await refreshBalance();
      }
      if (["completed", "failed"].includes(next) && !balanceRefreshPack.current.terminal) {
        balanceRefreshPack.current.terminal = true;
        await refreshBalance();
      }
      if (next === "completed") { await loadPackList({ background: true }); await retrievePack(id); return; }
      if (next === "failed") {
        const reason = statusData.error || statusData.error_code || statusData.code || "";
        setFailureReason(reason);
        if (reason === "insufficient_credits") setError("You need 20 AI credits to create a study pack. No study pack was generated.");
        else if (reason === "credit_service_unavailable") setError("We could not confirm your AI credit balance. Your notes were not processed. Please try again.");
        else setError("We could not create a study pack from these notes. Please check that the files are clear and contain readable notes, then try again.");
        await loadPackList({ background: true });
        return;
      }
      if (Date.now() - pollStartedAt.current >= MAX_POLL_DURATION_MS) {
        setPollPaused(true); setError("This is taking longer than expected. You can check again without starting over."); return;
      }
      pollTimer.current = setTimeout(() => checkStatus(id), STUDY_BUDDY_POLL_MS);
    } catch (requestError) {
      if (!mounted.current) return;
      if (isPublicSessionError(requestError)) expire();
      else {
        pollFailures.current += 1;
        if (pollFailures.current >= MAX_POLL_FAILURES) { setPollPaused(true); setError("We could not check the study pack status. Your notes were not uploaded again."); }
        else pollTimer.current = setTimeout(() => checkStatus(id), STUDY_BUDDY_POLL_MS);
      }
    } finally { pollRunning.current = false; }
  }, [clearPoll, expire, loadPackList, refreshBalance, retrievePack]);

  useEffect(() => {
    if (packId && !["completed", "failed"].includes(status) && !pollPaused && !pack) checkStatus(packId);
  }, [checkStatus, pack, packId, pollPaused, status]);

  function chooseFiles(event) {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    const message = validateStudyBuddyFiles(selected);
    if (message) { setFiles([]); setError(message); return; }
    setFiles(selected); setError("");
  }

  async function submit() {
    const message = validateStudyBuddyFiles(files);
    if (message) { setError(message); return; }
    if (!hasEnoughStudyBuddyCredits(balances)) {
      setFailureReason("insufficient_credits");
      setError("You need 20 AI credits to create a study pack.");
      return;
    }
    setUploading(true); setError(""); setFailureReason("");
    const form = new FormData();
    files.forEach((file) => form.append("files", file));
    try {
      const response = await axios.post("/api/study-buddy/packs", form, PUBLIC_SESSION_CONFIG);
      const data = payload(response);
      if (!data.pack_id) throw new Error("missing pack id");
      pollStartedAt.current = Date.now(); pollFailures.current = 0;
      balanceRefreshPack.current = { id: data.pack_id, initial: false, terminal: false };
      setPackId(data.pack_id); setStatus(data.status || "processing"); setPollPaused(false);
    } catch (requestError) {
      if (isPublicSessionError(requestError)) expire();
      else {
        const data = payload(requestError?.response);
        const reason = data.error || data.error_code || data.code || "";
        setFailureReason(reason);
        if (reason === "insufficient_credits") {
          await refreshBalance();
          setError("You need 20 AI credits to create a study pack. No study pack was generated.");
        } else if (reason === "credit_service_unavailable") {
          setError("We could not confirm your AI credit balance. Your notes were not processed. Please try again.");
        } else setError(safeError(requestError, "We could not start your study pack. Please try again."));
      }
    } finally { if (mounted.current) setUploading(false); }
  }

  function reset(retainFiles = false) {
    clearPoll(); pollRunning.current = false; pollFailures.current = 0; pollStartedAt.current = 0; balanceRefreshPack.current = null;
    if (!retainFiles) setFiles([]);
    setError(""); setFailureReason(""); setUploading(false); setPackId(""); setStatus(""); setPack(null); setExpanded(new Set()); setPollPaused(false);
  }

  const loadSelectedPack = useCallback(async (id, mode) => {
    setSelectedPackId(id); setSelectedMode(mode); setSelectedPack(null); setSelectedError(""); setPracticeSelection(null); setExpanded(new Set()); setSelectedLoading(true);
    try {
      const response = await axios.get(`/api/study-buddy/packs/${encodeURIComponent(id)}`, PUBLIC_SESSION_CONFIG);
      if (mounted.current) setSelectedPack(payload(response));
    } catch (requestError) {
      if (!mounted.current) return;
      if (isPublicSessionError(requestError)) expire();
      else {
        const data = payload(requestError?.response);
        const code = data.error || data.error_code || data.code || "";
        setSelectedError(code === "pack_not_found" || requestError?.response?.status === 404 ? "This study pack is no longer available." : "We could not load this study pack. Please try again.");
      }
    } finally { if (mounted.current) setSelectedLoading(false); }
  }, [expire]);

  function backToDashboard(refresh = false) {
    setSelectedPack(null); setSelectedPackId(""); setSelectedMode(""); setSelectedError(""); setSelectedLoading(false); setPracticeSelection(null); setExpanded(new Set());
    if (refresh) loadPackList({ background: true });
  }

  async function confirmDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true); setDeleteError(""); setNotice("");
    try {
      await axios.delete(`/api/study-buddy/packs/${encodeURIComponent(deleteTarget.pack_id)}`, PUBLIC_SESSION_CONFIG);
      const deletedId = deleteTarget.pack_id;
      setDeleteTarget(null);
      if (selectedPackId === deletedId) backToDashboard();
      await loadPackList({ background: savedPacks.length > 0 });
    } catch (requestError) {
      if (isPublicSessionError(requestError)) expire();
      else {
        const data = payload(requestError?.response);
        const code = data.error || data.error_code || data.code || "";
        if (code === "pack_not_found" || requestError?.response?.status === 404) {
          setDeleteTarget(null); setNotice("This study pack was already unavailable.");
          await loadPackList({ background: true });
        } else setDeleteError("We could not delete this study pack. Please try again.");
      }
    } finally { if (mounted.current) setDeleting(false); }
  }

  async function logout() {
    setLoggingOut(true);
    try { await logoutPublicSession(); reset(); clearListPoll(); setSavedPacks([]); setListLoaded(false); setSelectedPack(null); setSelectedPackId(""); setSelectedMode(""); setPracticeSelection(null); setDeleteTarget(null); setUser(null); setBalances(null); }
    catch (_) { setError("Logout failed. Please try again."); }
    finally { setLoggingOut(false); }
  }

  if (checking) return <main className="study-buddy-page">Checking your account…</main>;
  const processing = Boolean(packId) && status !== "completed" && status !== "failed";
  const enoughCredits = hasEnoughStudyBuddyCredits(balances);
  return <main className={`study-buddy-page ${embed ? "is-embed" : ""}`}>
    <header><h1>AI Study Buddy</h1><p>Upload your notes and AI Study Buddy will turn them into topics and practice questions.</p></header>
    {!user ? <><PublicSessionOtpForm description="Verify your email to create a study pack." onAuthenticated={(session) => { setUser(session.user); setBalances(session.balances); setError(""); }} />{error && <p className="study-error" role="alert">{error}</p>}</> : <>
      <PublicAccountSummary user={user} balances={balances} onLogout={logout} loggingOut={loggingOut} />
      {notice && <p className="study-notice" role="status">{notice}</p>}
      {!selectedMode && <StudyBuddyCreditGuide />}
      {selectedMode ? <section className="selected-pack-view">
        {selectedMode !== "edit" && <button type="button" className="study-button secondary back-button" onClick={() => backToDashboard(true)}>Back to My Study Packs</button>}
        {selectedLoading && <section className="study-card" role="status">Loading study pack…</section>}
        {selectedError && <section className="study-card" role="alert"><p className="study-error">{selectedError}</p><div className="study-actions"><button type="button" className="study-button secondary" onClick={() => loadSelectedPack(selectedPackId, selectedMode)}>Try Again</button><button type="button" className="study-button secondary" onClick={() => backToDashboard(true)}>Back to My Study Packs</button></div></section>}
        {selectedPack && selectedMode === "view" && <><PackResults pack={selectedPack} expanded={expanded} setExpanded={setExpanded} /><button type="button" className="study-button primary" onClick={()=>setSelectedMode("edit")}>Edit Study Pack</button></>}
        {selectedPack && selectedMode === "edit" && <StudyBuddyPackEditor pack={selectedPack} packId={selectedPackId} onCancel={()=>setSelectedMode("view")} onBack={()=>backToDashboard(true)} onReload={()=>loadSelectedPack(selectedPackId,"view")} onSessionExpired={expire} onSaved={(authoritative)=>{setSelectedPack(authoritative);setSelectedMode("view");setExpanded(new Set());loadPackList({background:true});}} />}
        {selectedPack && selectedMode === "practice" && <StudyBuddyPracticeScope pack={selectedPack} packId={selectedPackId} packType="private" selection={practiceSelection} setSelection={setPracticeSelection} onBack={() => backToDashboard()} onStart={onPracticeNavigate} />}
      </section> : <>
        <PublishedStudyPackLibrary balances={balances} setBalances={setBalances} onSessionExpired={expire} onPracticeStart={onPracticeNavigate} />
        <section className="study-dashboard-section" aria-labelledby="my-study-packs"><h2 id="my-study-packs">My Study Packs</h2>
          {listLoading && !listLoaded ? <section className="study-card" role="status">Loading your study packs…</section> : listError && !listLoaded ? <section className="study-card"><p className="study-error">{listError}</p><button type="button" className="study-button secondary" onClick={() => loadPackList()}>Try Again</button></section> : savedPacks.length === 0 ? <section className="study-card empty-state">You have not created any study packs yet. Upload your notes below to create your first study pack.</section> : <div className="pack-grid">{savedPacks.map((item, index) => {
            const itemId = typeof item.pack_id === "string" ? item.pack_id : "";
            const itemStatus = String(item.status || "").toLowerCase();
            const date = createdDate(item.created_at);
            return <article className="study-card saved-pack-card" key={itemId || `pack-${index}`}><h3>{typeof item.title === "string" && item.title.trim() ? item.title : "Study Pack"}</h3><p>{countLabel(item.topic_count, "Topic", "Topics")} · {countLabel(item.question_count, "Question", "Questions")}</p>{date && <p className="study-source">Created {date}</p>}{itemStatus === "processing" && <p className="pack-status" role="status">Processing</p>}{itemStatus === "failed" && <p className="study-error">Study pack generation was unsuccessful.</p>}<div className="study-actions">{itemStatus === "completed" && itemId && <><button type="button" className="study-button primary" onClick={() => loadSelectedPack(itemId, "practice")}>Start Practice</button><button type="button" className="study-button secondary" onClick={() => loadSelectedPack(itemId, "view")}>View</button></>} {itemId && <button type="button" className="study-button danger" onClick={() => { setDeleteTarget({ pack_id: itemId, title: typeof item.title === "string" && item.title.trim() ? item.title : "Study Pack" }); setDeleteError(""); }}>Delete</button>}</div></article>;
          })}</div>}
          {listError && listLoaded && <p className="study-error" role="status">{listError}</p>}
        </section>
        <section className="study-dashboard-section create-section" aria-labelledby="create-study-pack"><h2 id="create-study-pack">Create a New Study Pack</h2>
          {!packId && !pack && <section className="study-card"><h3>Upload your notes</h3><p className="study-cost"><strong>20 AI credits per study pack</strong></p><p>The cost is the same whether you upload one PDF or several note images.</p><label className="study-label" htmlFor="study-buddy-files">Choose one PDF with up to 10 pages or up to 10 note images</label><input id="study-buddy-files" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp" onChange={chooseFiles} disabled={uploading} />{files.length > 0 && <ol className="study-files">{files.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`}><span>{isPdf(file) ? file.name : `Image ${index + 1}: ${file.name}`} <small>({(file.size / 1024 / 1024).toFixed(2)} MB)</small></span><button type="button" className="study-button link" onClick={() => { const next=files.filter((_, i) => i !== index); setFiles(next); setError(""); }} disabled={uploading}>Remove</button></li>)}</ol>}<button type="button" className="study-button primary" onClick={submit} disabled={!files.length || !enoughCredits || uploading || processing}>Generate Study Pack — 20 Credits</button>{!enoughCredits && <><p className="study-error">You need 20 AI credits to create a study pack.</p><p className="study-purchase-guidance">Use Buy AI Credits above, then return and try again.</p></>}</section>}
          {processing && <section className="study-card processing" aria-live="polite"><h3>Creating your study pack…</h3><p>AI Study Buddy is reading your notes and preparing questions. This may take a few minutes.</p>{pollPaused && <button type="button" className="study-button secondary" onClick={() => checkStatus(packId, true)}>Check Again</button>}</section>}
          {error && <section className="study-card" role="alert"><p className="study-error">{error}</p>{failureReason === "insufficient_credits" && <p className="study-purchase-guidance">Use Buy AI Credits above, then return and try again.</p>}{status === "failed" && <button type="button" className="study-button secondary" onClick={() => reset(true)}>Try Again</button>}{status === "completed" && !pack && <button type="button" className="study-button secondary" onClick={() => retrievePack(packId)}>Try Again</button>}</section>}
          {pack && <><PackResults pack={pack} expanded={expanded} setExpanded={setExpanded} /><button type="button" className="study-button primary create-another" onClick={() => reset(false)}>Create Another Study Pack</button></>}
        </section>
      </>}
      {deleteTarget && <div className="delete-dialog-backdrop"><section className="delete-dialog study-card" role="dialog" aria-modal="true" aria-labelledby="delete-pack-title"><h2 id="delete-pack-title">Delete “{deleteTarget.title}”?</h2><p>This permanently removes the uploaded notes and generated study pack. The 20 credits used to generate it will not be refunded.</p>{deleteError && <p className="study-error" role="alert">{deleteError}</p>}<div className="study-actions"><button type="button" className="study-button secondary" onClick={() => { setDeleteTarget(null); setDeleteError(""); }} disabled={deleting}>Cancel</button><button ref={deleteButtonRef} type="button" className="study-button danger" onClick={confirmDelete} disabled={deleting} aria-busy={deleting}>{deleting ? "Deleting…" : "Delete Study Pack"}</button></div></section></div>}
    </>}
  </main>;
}

// The internally named V2 route is the validated controlled-turn default; V1 remains unlinked for rollback.
export default function StudyBuddyPage() { const navigate=useNavigate(); return <StudyBuddyWorkspace onPracticeNavigate={(reference)=>navigate(studyBuddyPracticeUrl("/study-buddy-practice-v2",reference),{state:{practice:reference}})} />; }
